/**
 * Installment math -- Rahber Corporation formula (derived from rate-list.xlsx).
 * Matches backend/src/utils/installmentCalculator.js exactly.
 *
 * ============================================================================
 * FORMULA (exact match to Excel rate-list.xlsx):
 * ============================================================================
 *
 *   Let:
 *     advance          = MRP x (advancePercent / 100)       (e.g. 25 -> 25%)
 *     financed         = MRP - advance
 *     totalProfit12    = financed x (markupRate / 100)      (markup on financed amount)
 *     perMonthProfit   = totalProfit12 / 12                 (always divides by 12 as baseline)
 *     totalPayable_N   = round100(MRP + (perMonthProfit x N)) (N = plan months)
 *     monthlyInst_N    = round100((totalPayable_N - advance) / N)
 *     (all calculated amounts are rounded to the nearest hundred)
 *
 * ============================================================================
 * CATEGORY MARKUP RATES:
 *   Appliances: 45%
 *   Bikes / EVS / Mobiles: 50%
 * ============================================================================
 */

// -- Default constants (matching Excel global defaults) ----------------------
export const DEFAULT_ADVANCE_PERCENT = 25;
export const DEFAULT_MARKUP_RATE = 45;
export const BIKES_EVS_MOBILE_MARKUP_RATE = 50;
export const DOWN_PAYMENT_OPTIONS = [25, 50, 75];
export const PLAN_DURATION_OPTIONS = [6, 9, 12];

export const CATEGORY_MARKUP_RATES = {
    // 50% markup categories
    bike:        50,
    bikes:       50,
    ev:          50,
    evs:         50,
    electric:    50,
    mobile:      50,
    phone:       50,
    smartphone:  50,
};

/**
 * Check if a product category requires 50% advance payment (Mobiles & Bikes only).
 * @param {string | object} category
 * @returns {boolean}
 */
export function is50PercentCategory(category) {
    if (!category) return false;
    const name = (typeof category === 'object' ? category.name || category.title || '' : String(category)).toLowerCase().trim();
    return (
        name === 'mobiles' ||
        name === 'mobile' ||
        name.includes('mobile') ||
        name.includes('phone') ||
        name.includes('smartphone') ||
        name === 'bikes' ||
        name === 'bike' ||
        name.includes('bike') ||
        name.includes('motorcycle') ||
        name.includes('ev')
    );
}

/**
 * Resolve the markup rate for a given category.
 * If category has a markup_rate field (from backend Category model), uses that.
 * Otherwise falls back to keyword matching or DEFAULT_MARKUP_RATE (45%).
 *
 * @param {string | object} [category]
 * @returns {number}
 */
export function resolveMarkupRate(category) {
    if (!category) return DEFAULT_MARKUP_RATE;
    if (typeof category === 'object') {
        const directRate = Number(category.markup_rate);
        if (Number.isFinite(directRate) && directRate >= 0) return directRate;
        const name = (category.name || category.title || '').toLowerCase().trim();
        for (const [kw, rate] of Object.entries(CATEGORY_MARKUP_RATES)) {
            if (name.includes(kw)) return rate;
        }
        return DEFAULT_MARKUP_RATE;
    }
    const str = String(category).toLowerCase().trim();
    for (const [kw, rate] of Object.entries(CATEGORY_MARKUP_RATES)) {
        if (str.includes(kw)) return rate;
    }
    return DEFAULT_MARKUP_RATE;
}

/**
 * Round to 4 decimal places -- matches Excel internal precision.
 */
export function round2(value) {
    return Math.round((Number(value) + Number.EPSILON) * 10000) / 10000;
}

/**
 * Ceiling to nearest whole rupee (standard hire-purchase ceiling formula).
 */
export function ceilRupee(value) {
    return Math.ceil(Number(value));
}

/**
 * Round an amount to the nearest 100 based on Rahber Corporation business rules:
 * - If the last two digits are less than 50, round down to the nearest hundred.
 *   (e.g., 12,525 -> 12,500; 12,549 -> 12,500)
 * - If the last two digits are 50 or greater, round up to the next hundred.
 *   (e.g., 12,550 -> 12,600; 12,566 -> 12,600)
 *
 * @param {number|string} value - amount to round
 * @returns {number} amount rounded to nearest hundred
 */
export function roundToNearest100(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return 0;
    }
    const result = Math.round(num / 100) * 100;
    return Object.is(result, -0) ? 0 : result;
}

export const roundToNearestHundred = roundToNearest100;

/**
 * Calculate installment plan details matching the Rahber Corporation Excel
 * rate-list formulas exactly.
 *
 * Supports both object argument and legacy positional arguments:
 *   calculateInstallment({ mrp, months, downPaymentPercent, markupRate, category })
 *   calculateInstallment(mrp, months, downPaymentPercent)
 */
export function calculateInstallment(arg1, legacyMonths, legacyDown) {
    let mrp, downPaymentPercent, months, markupRate, profitPercentage, category, categoryName;
    if (typeof arg1 === 'object' && arg1 !== null) {
        ({
            mrp,
            downPaymentPercent,
            months,
            markupRate,
            profitPercentage,
            category,
            categoryName,
        } = arg1);
    } else {
        mrp = arg1;
        months = legacyMonths;
        downPaymentPercent = legacyDown;
    }

    const mrpN = Number(mrp);
    if (!Number.isFinite(mrpN) || mrpN <= 0) {
        return { ok: false, error: 'MRP must be a positive number' };
    }

    const catRef = category || categoryName;
    const isSpecialCategory = is50PercentCategory(catRef);

    const hasExplicitDownPayment =
        downPaymentPercent !== undefined &&
        downPaymentPercent !== null &&
        downPaymentPercent !== '';

    let dp;
    if (hasExplicitDownPayment) {
        dp = Number(downPaymentPercent);
    } else {
        dp = isSpecialCategory ? 50 : DEFAULT_ADVANCE_PERCENT;
    }

    if (!Number.isFinite(dp) || dp < 0 || dp > 100) {
        return {
            ok: false,
            error: 'Down payment percentage must be between 0 and 100',
        };
    }

    const m = parseInt(months !== undefined && months !== null ? months : 12, 10);
    if (!Number.isFinite(m) || m < 1) {
        return {
            ok: false,
            error: 'Installment plan must be at least 1 month',
        };
    }

    // Effective markup rate (always prioritize explicit plan markup / profit percentage):
    const hasExplicitMarkup =
        markupRate !== undefined && markupRate !== null && markupRate !== '';
    const hasExplicitProfit =
        profitPercentage !== undefined &&
        profitPercentage !== null &&
        profitPercentage !== '';

    let rate;
    if (hasExplicitMarkup && Number.isFinite(Number(markupRate)) && Number(markupRate) >= 0) {
        rate = Number(markupRate);
    } else if (hasExplicitProfit && Number.isFinite(Number(profitPercentage)) && Number(profitPercentage) >= 0) {
        rate = Number(profitPercentage);
    } else if (m === 4) {
        rate = 0; // 4-month 0% markup promo when no explicit rate is provided
    } else {
        rate = resolveMarkupRate(catRef);
    }

    // Core Excel formula:
    //   advance        = MRP x (dp / 100)
    //   financed       = MRP - advance
    //   totalProfit12  = financed x (rate / 100)
    //   perMonthProfit = totalProfit12 / 12
    //   totalPayable   = MRP + (perMonthProfit x N)
    //   financedTotal  = totalPayable - advance
    //   monthly        = (financedTotal / N) rounded to nearest 100
    //
    // All calculated amounts are rounded to the nearest 100 using roundToNearest100.
    const downPaymentAmount  = roundToNearest100((mrpN * dp) / 100);
    const amountAfterDown    = roundToNearest100(mrpN - downPaymentAmount);       // financed balance
    const totalProfit12Raw   = (amountAfterDown * rate) / 100;                    // full float64
    const perMonthProfitRaw  = totalProfit12Raw / 12;                             // full float64
    const totalPayable       = roundToNearest100(mrpN + (perMonthProfitRaw * m));
    const financedTotal      = roundToNearest100(totalPayable - downPaymentAmount);
    const profitAmount       = roundToNearest100(totalPayable - mrpN);

    // Rounded display values (do NOT use these for further arithmetic)
    const totalProfit12  = roundToNearest100(totalProfit12Raw);
    const perMonthProfit = roundToNearest100(perMonthProfitRaw);

    // Monthly installment rounded to the nearest 100
    const monthlyInstallment = financedTotal > 0
        ? roundToNearest100(financedTotal / m)
        : 0;

    return {
        ok: true,
        downPaymentAmount,
        advance: downPaymentAmount,
        amountAfterDown,
        totalProfit12,
        perMonthProfit,
        profitAmount,
        financedTotal,
        monthlyInstallment,
        monthly: monthlyInstallment,
        totalPayable,
        remainingMonths: m,
        markupRate: rate,
        downPaymentPercent: dp,
        is50PercentCategory: isSpecialCategory,
    };
}

/**
 * Build a full installment plan matrix for a product, matching all Excel columns.
 */
export function buildInstallmentMatrix({
    mrp,
    category,
    categoryName,
    markupRate: explicitRate,
    advanceOptions = DOWN_PAYMENT_OPTIONS,
    monthOptions = PLAN_DURATION_OPTIONS,
} = {}) {
    const cat = category || categoryName;
    const rate = Number.isFinite(Number(explicitRate))
        ? Number(explicitRate)
        : resolveMarkupRate(cat);

    const isSpecial = is50PercentCategory(cat);
    const effectiveAdvances = isSpecial ? [50] : advanceOptions;

    const rows = [];
    for (const dp of effectiveAdvances) {
        for (const m of monthOptions) {
            const result = calculateInstallment({
                mrp,
                downPaymentPercent: dp,
                months: m,
                markupRate: rate,
                category: cat,
            });
            if (!result.ok) continue;
            rows.push({
                downPaymentPercent: dp,
                months: m,
                downPaymentAmount: result.downPaymentAmount,
                advance: result.advance,
                amountAfterDown: result.amountAfterDown,
                totalProfit12: result.totalProfit12,
                perMonthProfit: result.perMonthProfit,
                profitAmount: result.profitAmount,
                financedTotal: result.financedTotal,
                monthlyInstallment: result.monthlyInstallment,
                monthly: result.monthly,
                totalPayable: result.totalPayable,
            });
        }
    }
    return rows;
}

export function normalizeDownPercents(options, category) {
    if (!Array.isArray(options) || options.length === 0) {
        return is50PercentCategory(category) ? [50] : [];
    }
    const sorted = [...new Set(options)]
        .map((p) => Number(p))
        .filter((p) => Number.isFinite(p) && p >= 0 && p < 100)
        .sort((a, b) => a - b);
    return sorted;
}

export function normalizePlanMonths(months) {
    if (!Array.isArray(months) || months.length === 0) return [];
    const sorted = [...new Set(months)]
        .map((m) => Math.round(Number(m)))
        .filter((m) => Number.isInteger(m) && m >= 1)
        .sort((a, b) => a - b);
    return sorted;
}

const LEGACY_DEFAULT_MONTHS = 12;

/**
 * Normalize raw installment plan tiers configured by the admin on a product.
 * Supports percentage-based tiers ({ down_payment_percent, months, profit_percentage })
 * as well as extended/direct amount plan objects ({ months, advance_amount, monthly_installment, total_payable }).
 */
export function normalizeInstallmentPlanTiers(profits, options = {}) {
    if (!Array.isArray(profits) || profits.length === 0) return [];

    const mrp = Number(options.mrp || 0);
    const fallbackMonthsList = Array.isArray(options.fallbackMonthsList)
        ? options.fallbackMonthsList
              .map((m) => Math.round(Number(m)))
              .filter((m) => Number.isInteger(m) && m >= 1)
        : [];
    const productMarkupRate =
        options.productMarkupRate !== undefined &&
        options.productMarkupRate !== null &&
        options.productMarkupRate !== '' &&
        Number.isFinite(Number(options.productMarkupRate)) &&
        Number(options.productMarkupRate) >= 0
            ? Number(options.productMarkupRate)
            : null;

    const dedupMap = new Map();

    for (const row of profits) {
        if (!row || typeof row !== 'object') continue;
        if (
            row.is_active === false ||
            row.active === false ||
            row.enabled === false ||
            row.status === 'inactive'
        ) {
            continue;
        }

        const rawDpPercent =
            row.down_payment_percent ??
            row.downPaymentPercent ??
            row.advance_percent ??
            row.advancePercent ??
            row.down_payment_percentage ??
            row.advance_percentage;

        const rawDownAmount =
            row.down_payment_amount ??
            row.downPaymentAmount ??
            row.advance_amount ??
            row.advanceAmount ??
            row.advance ??
            row.down_payment;

        const rawProfitPercent =
            row.profit_percentage ??
            row.profitPercentage ??
            row.markup_rate ??
            row.markupRate ??
            row.interest_rate ??
            row.interestRate ??
            row.markup ??
            row.profit_rate;

        const rawMonthlyAmount =
            row.monthly_installment ??
            row.monthlyInstallment ??
            row.monthly_amount ??
            row.monthlyAmount ??
            row.monthly_payment ??
            row.emi;

        const rawTotalAmount =
            row.total_payable ??
            row.totalPayable ??
            row.total_amount ??
            row.totalAmount ??
            row.total_price;

        const rawMonths =
            row.months ??
            row.duration ??
            row.tenure ??
            row.plan_months ??
            row.installment_months ??
            row.duration_months;

        const explicitDownAmount =
            rawDownAmount !== undefined &&
            rawDownAmount !== null &&
            rawDownAmount !== '' &&
            Number.isFinite(Number(rawDownAmount)) &&
            Number(rawDownAmount) >= 0
                ? Number(rawDownAmount)
                : null;

        const explicitMonthlyAmount =
            rawMonthlyAmount !== undefined &&
            rawMonthlyAmount !== null &&
            rawMonthlyAmount !== '' &&
            Number.isFinite(Number(rawMonthlyAmount)) &&
            Number(rawMonthlyAmount) > 0
                ? Number(rawMonthlyAmount)
                : null;

        const explicitTotalAmount =
            rawTotalAmount !== undefined &&
            rawTotalAmount !== null &&
            rawTotalAmount !== '' &&
            Number.isFinite(Number(rawTotalAmount)) &&
            Number(rawTotalAmount) > 0
                ? Number(rawTotalAmount)
                : null;

        let dp = NaN;
        if (
            rawDpPercent !== undefined &&
            rawDpPercent !== null &&
            rawDpPercent !== '' &&
            Number.isFinite(Number(rawDpPercent))
        ) {
            dp = Number(rawDpPercent);
        } else if (explicitDownAmount !== null && mrp > 0) {
            dp = Math.round(((explicitDownAmount / mrp) * 100) * 100) / 100;
        }

        if (!Number.isFinite(dp) || dp < 0 || dp >= 100) {
            continue;
        }

        const hasProfitPercent =
            rawProfitPercent !== undefined &&
            rawProfitPercent !== null &&
            rawProfitPercent !== '' &&
            Number.isFinite(Number(rawProfitPercent)) &&
            Number(rawProfitPercent) >= 0;

        let pp = null;
        if (hasProfitPercent) {
            pp = Number(rawProfitPercent);
        } else if (explicitMonthlyAmount !== null) {
            pp = null;
        } else if (productMarkupRate !== null) {
            pp = productMarkupRate;
        } else {
            continue;
        }

        let monthsList = [];
        if (rawMonths !== undefined && rawMonths !== null && rawMonths !== '') {
            const m = Math.round(Number(rawMonths));
            if (!Number.isFinite(m) || m < 1) {
                continue;
            }
            monthsList = [m];
        } else if (fallbackMonthsList.length > 0) {
            monthsList = fallbackMonthsList;
        } else {
            monthsList = [LEGACY_DEFAULT_MONTHS];
        }

        const rawLabel = row.label ?? row.title ?? row.plan_name ?? '';
        const label = typeof rawLabel === 'string' ? rawLabel.trim() : '';

        for (const m of monthsList) {
            const key = `${dp}_${m}`;
            dedupMap.set(key, {
                down_payment_percent: dp,
                profit_percentage: pp,
                months: m,
                down_payment_amount: explicitDownAmount,
                monthly_installment: explicitMonthlyAmount,
                total_payable: explicitTotalAmount,
                label,
            });
        }
    }

    const tiers = Array.from(dedupMap.values());
    tiers.sort((a, b) => {
        if (a.months !== b.months) return a.months - b.months;
        return a.down_payment_percent - b.down_payment_percent;
    });
    return tiers;
}

/**
 * Extract strictly the admin-configured installment plans for a specific product.
 * Never returns hardcoded default plans or category fallback guesses.
 * If the product has installments disabled or no plans configured, returns [].
 *
 * @param {object} product
 * @returns {Array}
 */
export function getProductInstallmentPlans(product) {
    if (!product || typeof product !== 'object') return [];

    if (
        product.installment_product === false ||
        product.has_installments === false ||
        product.is_installment_available === false
    ) {
        return [];
    }

    const mrp = Number(product.mrp ?? product.price ?? 0);
    if (!Number.isFinite(mrp) || mrp <= 0) {
        return [];
    }

    const candidatePlanArrays = [
        product.installment_plans,
        product.installmentPlans,
        product.installment_profits,
        product.installmentProfits,
        product.plans,
    ];

    const configuredArray = candidatePlanArrays.find(
        (arr) => Array.isArray(arr) && arr.length > 0
    );

    // If installment_product flag is absent/falsy and no explicit plan array exists, return []
    if (!product.installment_product && !configuredArray) {
        return [];
    }

    const productMonthsRaw =
        product.installment_plan_months ??
        product.plan_months ??
        product.durations;
    const productFallbackMonths = Array.isArray(productMonthsRaw)
        ? normalizePlanMonths(productMonthsRaw)
        : Number.isFinite(Number(product.installment_months ?? product.months)) &&
          Number(product.installment_months ?? product.months) >= 1
        ? [Math.round(Number(product.installment_months ?? product.months))]
        : [];

    if (configuredArray) {
        return normalizeInstallmentPlanTiers(configuredArray, {
            mrp,
            fallbackMonthsList: productFallbackMonths,
            productMarkupRate: product.markup_rate,
        });
    }

    // Support products where admin explicitly saved down_payment_options + installment_plan_months + markup_rate directly on the product document
    const productDownOptions = Array.isArray(product.down_payment_options)
        ? product.down_payment_options
              .map((p) => Number(p))
              .filter((p) => Number.isFinite(p) && p >= 0 && p < 100)
        : [];

    const hasExplicitProductMarkup =
        product.markup_rate !== undefined &&
        product.markup_rate !== null &&
        product.markup_rate !== '' &&
        Number.isFinite(Number(product.markup_rate)) &&
        Number(product.markup_rate) >= 0;

    if (
        productDownOptions.length > 0 &&
        productFallbackMonths.length > 0 &&
        hasExplicitProductMarkup
    ) {
        const synthesized = [];
        for (const dp of [...new Set(productDownOptions)]) {
            for (const m of productFallbackMonths) {
                synthesized.push({
                    down_payment_percent: dp,
                    profit_percentage: Number(product.markup_rate),
                    months: m,
                });
            }
        }
        return normalizeInstallmentPlanTiers(synthesized, { mrp });
    }

    return [];
}

/**
 * Calculate the exact monetary breakdown for a specific product and one of its configured plan tiers.
 * Preserves Rahber's exact rounding & Excel rate-list formula while honoring explicit admin plan overrides.
 *
 * @param {object} product
 * @param {object} planTier
 * @returns {object}
 */
export function calculateProductPlan(product, planTier) {
    if (!product || !planTier) {
        return { ok: false, error: 'Missing product or installment plan tier' };
    }

    const mrp = Number(product.mrp ?? product.price ?? 0);
    if (!Number.isFinite(mrp) || mrp <= 0) {
        return { ok: false, error: 'MRP must be a positive number' };
    }

    const months = Math.round(Number(planTier.months));
    const dp = Number(planTier.down_payment_percent);

    // If the plan tier has explicit monthly installment without profit_percentage, compute from explicit amounts
    if (
        (planTier.profit_percentage === null || planTier.profit_percentage === undefined) &&
        Number.isFinite(planTier.monthly_installment) &&
        planTier.monthly_installment > 0
    ) {
        const downPaymentAmount = Number.isFinite(planTier.down_payment_amount)
            ? planTier.down_payment_amount
            : roundToNearest100((mrp * dp) / 100);
        const monthlyInstallment = planTier.monthly_installment;
        const totalPayable = Number.isFinite(planTier.total_payable)
            ? planTier.total_payable
            : roundToNearest100(downPaymentAmount + monthlyInstallment * months);
        const financedTotal = Math.max(0, totalPayable - downPaymentAmount);
        const profitAmount = Math.max(0, totalPayable - mrp);
        const amountAfterDown = Math.max(0, mrp - downPaymentAmount);

        return {
            ok: true,
            downPaymentAmount,
            advance: downPaymentAmount,
            amountAfterDown,
            profitAmount,
            financedTotal,
            monthlyInstallment,
            monthly: monthlyInstallment,
            totalPayable,
            remainingMonths: months,
            planMonths: months,
            markupRate: null,
            downPaymentPercent: dp,
            label: planTier.label || '',
        };
    }

    const baseCalc = calculateInstallment({
        mrp,
        downPaymentPercent: dp,
        months,
        markupRate: planTier.profit_percentage,
        category: product.category,
    });

    if (!baseCalc.ok) {
        return baseCalc;
    }

    const downPaymentAmount = Number.isFinite(planTier.down_payment_amount)
        ? planTier.down_payment_amount
        : baseCalc.downPaymentAmount;
    const monthlyInstallment = Number.isFinite(planTier.monthly_installment)
        ? planTier.monthly_installment
        : baseCalc.monthlyInstallment;
    const totalPayable = Number.isFinite(planTier.total_payable)
        ? planTier.total_payable
        : baseCalc.totalPayable;

    return {
        ...baseCalc,
        ok: true,
        downPaymentAmount,
        advance: downPaymentAmount,
        monthlyInstallment,
        monthly: monthlyInstallment,
        totalPayable,
        financedTotal: Math.max(0, totalPayable - downPaymentAmount),
        profitAmount: Math.max(0, totalPayable - mrp),
        remainingMonths: months,
        planMonths: months,
        markupRate: baseCalc.markupRate,
        downPaymentPercent: dp,
        label: planTier.label || '',
    };
}

/**
 * Resolve the lowest valid monthly installment plan configured on a product (for catalog cards & quick summaries).
 * Returns null if the product has no configured installment plans.
 *
 * @param {object} product
 * @returns {object|null}
 */
export function getStartingProductInstallment(product) {
    const plans = getProductInstallmentPlans(product);
    if (plans.length === 0) return null;

    let bestCalc = null;
    for (const tier of plans) {
        const calc = calculateProductPlan(product, tier);
        if (!calc.ok || calc.monthlyInstallment <= 0) continue;
        if (
            !bestCalc ||
            calc.monthlyInstallment < bestCalc.monthlyInstallment ||
            (calc.monthlyInstallment === bestCalc.monthlyInstallment &&
                calc.downPaymentAmount < bestCalc.downPaymentAmount)
        ) {
            bestCalc = { ...calc, planTier: tier };
        }
    }
    return bestCalc;
}
